import { Routes } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { AdminEffects } from 'src/app/admin/store/admin.effects';
import { AdminReducers } from 'src/app/admin/store/admin.reducers';
import { AdminFeatureKey } from 'src/app/admin/store/admin.selectors';
import { clinicalEffects } from 'src/app/clinical/store/effects';
import { clinicalReducers } from 'src/app/clinical/store/reducers';
import { clinicalFeatureKey } from 'src/app/clinical/store/selectors';
import { InAppNotificationsTabsComponent } from 'src/app/in-app-notifications/in-app-notification-tabs/in-app-notifications-tabs.component';
import { PARTNER_NETWORK_PROVIDERS } from 'src/app/partner-network/partner-network-routing.module';
import { CatchAllComponent } from './catchall.component';
import { AuthGuard } from './core/guards/auth.guard';
import { ComplianceGuard } from './core/guards/compliance.guard';
import { myDelegationsGuard } from './core/guards/my-delegations.guard';
import { DatadogLoggerComponent } from './datadog-logger/datadog-logger.component';
import { providePermPostOfferWorkflowStore } from './root-store';
import { WelcomeComponent } from './welcome.component';

export const appRoutes: Routes = [
    {
        path: 'welcome/home',
        canActivate: [AuthGuard],
        component: WelcomeComponent,
        title: 'Home'
    },
    {
        path: 'devops/logger',
        component: DatadogLoggerComponent
    },
    {
        path: 'in-app-notifications',
        canActivate: [AuthGuard],
        component: InAppNotificationsTabsComponent
    },
    {
        path: 'delegations',
        canActivate: [AuthGuard, myDelegationsGuard],
        loadChildren: () => import('src/app/delegations/delegations.routes').then((m) => m.DelegationsRoutes)
    },
    {
        path: 'admin',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/admin/admin.routes').then((m) => m.AdminRoutes),
        providers: [provideState(AdminFeatureKey, AdminReducers), provideEffects(AdminEffects)]
    },
    {
        path: 'workforce-disruption',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/workforce-disruption/workforce-disruption.module').then((m) => m.WorkforceDisruptionModule)
    },
    {
        path: 'accountmanagers',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/accountmanagers/accountmanagers.routes').then((m) => m.ACCOUNT_MANAGER_ROUTES),
        providers: [providePermPostOfferWorkflowStore()]
    },
    {
        path: 'billing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/billing/billing.module').then((m) => m.BillingModule)
    },
    {
        path: 'compliance',
        canActivate: [AuthGuard],
        canLoad: [ComplianceGuard],
        loadChildren: () => import('src/app/compliance/compliance.module').then((m) => m.ComplianceModule)
    },
    {
        path: 'contacts',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/shared/components/contacts/contact.routes').then((m) => m.CONTACT_ROUTES)
    },
    {
        path: 'credentialing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/credentialing/credentialing.module').then((m) => m.CredentialingModule)
    },
    {
        path: 'esign',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/esign/esign.module').then((m) => m.EsignModule)
    },
    {
        path: 'facility-contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/facility-contracts/facility-contracts.module').then((m) => m.FacilityContractsModule)
    },
    {
        path: 'travel-contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/travel-contracts/travel-contracts.module').then((m) => m.TravelContractsModule)
    },
    {
        path: 'documents',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/documents/documents.module').then((m) => m.DocumentsModule)
    },
    {
        path: 'margin-calculator',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/margin-calculator/margin-calculator.module').then((m) => m.MarginCalculatorModule)
    },
    {
        path: 'subcontractor-margin-calculator',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/subcontractor-margin-calculator/subcontractor-margin-calculator.module').then(
                (m) => m.SubcontractorMarginCalculatorModule
            )
    },
    {
        path: 'payroll/facility-profiles',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/facility-profiles/facility-profiles.routes').then((m) => m.FACILITY_PROFILES_ROUTES)
    },
    {
        path: 'account-receivables/facilities',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/account-receivables/account-receivables.routes').then((m) => m.ACCT_RECEIVABLES_ROUTES)
    },
    {
        path: 'account-receivables/contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/account-receivables/contracts-grid/contracts-grid.routes').then(
                (m) => m.ACC_RECEIVABLES_CONTRACTS_GRID_ROUTES
            )
    },
    {
        path: 'account-receivables/purchase-orders',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/billing/purchase-orders/purchase-orders-routing.module').then((m) => m.PurchaseOrdersRoutes)
    },
    {
        path: 'payroll/facility-profiles/billing-groups',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/billing-groups/billing-group.module').then((m) => m.BillingGroupModule)
    },
    {
        path: 'payroll/contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/payroll/search-contracts/search-contracts.module').then(
                (m) => m.PayrollSearchContractsModule
            )
    },
    {
        path: 'payroll/timecard-shifts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/payroll/timecard-shifts/timecard-shifts.module').then((m) => m.TimecardShiftsModule)
    },
    {
        path: 'payroll',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/payroll/payroll.module').then((m) => m.PayrollModule)
    },
    {
        path: 'leadqueue',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/lead-queue/lead-queue.routes').then((m) => m.LeadQueueRoutes)
    },
    {
        path: 'clinical',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/clinical/clinical.routes').then((m) => m.ClinicalRoutes),
        providers: [provideState(clinicalFeatureKey, clinicalReducers), provideEffects(clinicalEffects)]
    },
    {
        path: 'locums',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/locums/locums.routes').then((m) => m.ModuleRoutes)
    },
    {
        path: 'recruiting',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/recruiting/recruiting.module').then((m) => m.RecruitingModule)
    },
    {
        path: 'travelx',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/travelx/travelx.routes').then((m) => m.TravelXRoutes)
    },
    {
        path: 'travelxnew',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/travelxnew/travelxnew.routes').then((m) => m.TRAVELXNEW_ROUTES)
    },
    {
        path: 'partnernetwork',
        canActivate: [AuthGuard],
        providers: [...PARTNER_NETWORK_PROVIDERS],
        loadChildren: () =>
            import('src/app/partner-network/partner-network-routing.module').then((m) => m.PARTNER_NETWORK_ROUTES)
    },
    {
        path: 'perdiem',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/perdiem/perdiem.module').then((m) => m.PerdiemModule)
    },
    {
        path: 'commissions',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/commissions/commissions.routes').then((m) => m.COMMISSIONS_ROUTES)
    },
    {
        path: 'corporate-directory',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/corporate-directory/corporate-directory.routes').then((m) => m.CorporateDirectoryRoutes)
    },
    {
        path: 'reporting',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/reporting/reporting.routes').then((m) => m.ReportingRoutes)
    },
    {
        path: 'perdiem-recruiting',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/perdiem-recruiting/perdiem-recruiting.module').then((m) => m.PerDiemRecruitingModule)
    },
    {
        path: 'housing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/housing/housing.routes').then((m) => m.HousingRoutes)
    },
    {
        path: 'vendors',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/housing/vendors/vendors.routes').then((m) => m.VendorRoutes)
    },
    {
        path: 'references',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/references/references.module').then((m) => m.ReferencesModule)
    },
    {
        path: 'martech',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/martech/martech.routes').then((m) => m.martechModuleRoutes)
    },
    {
        path: 'access-control',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/access-control/access-control.module').then((m) => m.AccessControlModule)
    },
    {
        path: 'devops',
        loadChildren: () => import('src/app/devops/devops.routes').then((m) => m.DevOpsModuleRoutes)
    },
    {
        path: 'signin',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: 'sso',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: 'logout',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: 'wfd-agent',
        loadChildren: () => import('src/app/wfd-agent/wfd-agent.routes').then((m) => m.WfdAgentRoutes)
    },
    {
        path: '',
        redirectTo: '/welcome/home',
        pathMatch: 'full'
    },
    {
        path: '**',
        component: CatchAllComponent
    }
];
